import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Project, ModuleType } from '@/types'
import { Building2, ArrowRight, Settings2 } from 'lucide-react'
import { isSuperAdmin, isAdmin, hasAdminPrivileges } from '@/utils/permissions'

interface ProjectCardProps {
  project: Project
  user: any
}

const MODULE_CONFIG: Record<ModuleType, { label: string; color: string }> = {
  build: { label: 'Build', color: 'bg-blue-400' },
  review: { label: 'Review', color: 'bg-amber-400' },
  manage: { label: 'Manage', color: 'bg-emerald-400' },
}

export const ProjectCard = ({ project, user }: ProjectCardProps) => {
  const { t } = useTranslation(['dashboard', 'common'])
  const navigate = useNavigate()
  const userHasAdminPrivileges = hasAdminPrivileges(user)

  const enabledModules = project.enabled_modules || ['build', 'review', 'manage']

  const handleClick = () => {
    navigate(`/project/${project.id}`)
  }

  return (
    <div
      onClick={handleClick}
      className="group bg-framed-neutral-100 rounded-2xl p-5 hover:bg-framed-neutral-200 transition-colors duration-150 cursor-pointer"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-3">
          <h3 className="text-base font-semibold text-framed-neutral-800 truncate group-hover:text-framed-neutral-800-300 transition-colors">
            {project?.name || t('dashboard:project.untitled')}
          </h3>
          {isSuperAdmin(user) && (project as any).agencies?.name && (
            <p className="text-xs text-framed-neutral-500 mt-0.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {(project as any).agencies.name}
            </p>
          )}
          {!isSuperAdmin(user) && isAdmin(user) && project.agency_id !== user?.agency_id && (
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium">
              <Building2 className="w-3 h-3" />
              {t('dashboard:project.externalAgency')}{(project as any).agencies?.name ? ` · ${(project as any).agencies.name}` : ''}
            </span>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-framed-neutral-400 group-hover:text-framed-neutral-600 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
      </div>

      {/* Module Pills */}
      <div className="flex items-center gap-1.5 mb-3">
        {enabledModules.map((mod) => {
          const config = MODULE_CONFIG[mod]
          if (!config) return null
          return (
            <span
              key={mod}
              className="inline-flex items-center gap-1 text-[11px] text-framed-neutral-500"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${config.color}`} />
              {config.label}
            </span>
          )
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-framed-neutral-400">
          {new Date(project.updated_at).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short'
          })}
        </span>
        {userHasAdminPrivileges && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/project/${project.id}/settings`)
            }}
            className="p-1 rounded text-framed-neutral-400 hover:text-framed-neutral-800 hover:bg-framed-neutral-100 transition-colors"
            title={t('dashboard:project.settings') || 'Settings'}
          >
            <Settings2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
